import React, { useEffect, useState } from 'react';
import './fields_creator.css';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getUser } from '../../store/user.reducer';

import { Input, Select, Checkbox, DatePicker, Slider, Tooltip, Col, SliderSingleProps } from 'antd';
import { LockOutlined } from '@ant-design/icons';

const FieldsCreator = (props: any) => {

  const {t} = useTranslation();

  const user = useSelector(getUser);

  const [formData, setFormData]: [any, any] = useState({});

  useEffect(() => {
    if (user.profile) {
      const data: any = {};

      Object.keys(user.profile)
        .filter(itemKey => itemKey !== 'topic')
        .forEach((itemKey: any) => {
          data[itemKey] = {
            value: user.profile[itemKey]
          }
        })

      setFormData((d: any) => {
        const updatedData = {
          ...d,
          advancedFields: {
            ...data
          }
        };
        props.propagateFormData(updatedData);
        return updatedData;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile]);

  const updateFormData = (name: string, value: any, fieldGroup: string, isSensitive: boolean = false) => {
    // If value is empty, completely remove the field from formData state
    setFormData((data: any) => {
      if (value === '') {
        delete data[fieldGroup][name]
        return {...data};
      } else {
        return {
          ...data,
          [fieldGroup]: {
            ...data[fieldGroup],
            [name]: {
              value,
              isSensitive
            }
          }
        }
      }
    });
  }

  const handleInputChange = (event: any, fieldGroup: string, isSensitive: boolean = false) => {
    const { name, value } = event.target;
    updateFormData(name, value, fieldGroup, isSensitive);
    props.propagateInputData(name, value, fieldGroup, isSensitive);
  };

  const handleTagsChange = (value: string[], name: string, fieldGroup: string) => {
    updateFormData(name, value, fieldGroup);
    props.propagateInputData(name, value, fieldGroup);
  }

  const InputTypes = {
    text: 'text',
    select: 'select',
    date: 'date',
    tag: 'tag',
    number: 'number',
    checkboxList: 'checkboxList',
    range: 'range',
  };

  const dropdownBuilder = (data: any, fieldName: string, fieldGroup: string) => {
    return (
      <Select
        className="w-100"
        placeholder={t(`formBuilder.${fieldName}`)}
        value={formData[fieldGroup]?.[fieldName]?.value || undefined}
        onChange={(value) => {
          updateFormData(fieldName, value, fieldGroup);
          props.propagateInputData(fieldName, value, fieldGroup);
        }}
      >
        {data.list.map((option: any, index: number) => (
          <Select.Option value={option.value} key={index}>
            {t(`formBuilder.${option.value}`)}
          </Select.Option>
        ))}
      </Select>
    );
  }

  const checkboxListBuilder = (data: any, fieldName: string, fieldGroup: string) => {
    return data.list.map((checkbox: any, index: number) => (
      <Checkbox
        key={index}
        onChange={(e) => {
          const value = e.target.checked ? 'true' : 'false';
          updateFormData(checkbox.name, value, fieldGroup);
          props.propagateInputData(checkbox.name, value, fieldGroup);
        }}
        checked={formData[fieldGroup]?.[checkbox.name]?.value === 'true'}
      >
        {t(`formBuilder.${checkbox.name}`)}
      </Checkbox>
    ));
  }

  const createFields = (fields: any, fieldGroup: string) => {
    if (!fields) return <></>
    return Object.entries(fields)
      .filter(([fieldName]) => !props.fieldsToCreate || (props.fieldsToCreate && props.fieldsToCreate.includes(fieldName)))
      .map(([fieldName, fieldData]: [any, any]) => {
        const { type, length, sensitive } = fieldData;
        const placeholder = t(`formBuilder.${fieldName}`);
        const inputId = `${fieldName}`;
        let render;
        const value = formData[fieldGroup] && formData[fieldGroup][fieldName] ? formData[fieldGroup][fieldName].value : '';

        switch (type) {
          case InputTypes.text:
            render = (
              <div className={`position-relative ${sensitive ? 'private-field' : ''}`}>
                <Input
                  className={`${sensitive ? 'pe-5' : ''}`}
                  key={inputId}
                  placeholder={placeholder}
                  id={inputId}
                  name={fieldName}
                  maxLength={length}
                  onChange={(event) => handleInputChange(event, fieldGroup, sensitive)}
                  value={value || ''}
                />
                {sensitive && (
                  <Tooltip title={t('formBuilder.securedField')}>
                    <LockOutlined style={{ position: 'absolute', right: 8, top: 10 }} />
                  </Tooltip>
                )}
              </div>
            );
            break;
          case InputTypes.select:
            render = dropdownBuilder(fieldData, fieldName, fieldGroup);
            break;
          case InputTypes.checkboxList:
            render = checkboxListBuilder(fieldData, fieldName, fieldGroup);
            break;
          case InputTypes.date:
            render = (
              <DatePicker
                key={inputId}
                placeholder={placeholder}
                id={inputId}
                name={fieldName}
                onChange={(date, dateString) => {
                  updateFormData(fieldName, dateString, fieldGroup);
                  props.propagateInputData(fieldName, dateString, fieldGroup);
                }}
                style={{ width: '100%' }}
                value={value ? new Date(value) : null}
              />
            );
            break;
          case InputTypes.tag:
            const tags = formData[fieldGroup] && formData[fieldGroup][fieldName] ? formData[fieldGroup][fieldName].value : [];
            render = (
              <div className={`position-relative ${sensitive ? 'private-field' : ''}`}>
                <Select
                  mode="tags"
                  value={tags}
                  onChange={(tags: string[]) => handleTagsChange(tags, fieldName, fieldGroup)}
                  placeholder={placeholder}
                  style={{ width: '100%' }}
                />
                {sensitive && (
                  <Tooltip title="This field will not be shared with our AI partners.">
                    <LockOutlined style={{ position: 'absolute', right: 8, top: 10 }} />
                  </Tooltip>
                )}
              </div>
            );
            break;
          case InputTypes.number:
            render = (
              <Input
                key={inputId}
                placeholder={placeholder}
                id={inputId}
                name={fieldName}
                type="number"
                maxLength={length}
                onChange={(event) => handleInputChange(event, fieldGroup)}
                value={value || ''}
              />
            );
            break;
          case InputTypes.range:
            const marks: SliderSingleProps['marks'] = {
              [fieldData.min]: t(`formBuilder.${fieldData.minLabel}`),
              [fieldData.mid]: t(`formBuilder.${fieldData.midLabel}`),
              [fieldData.max]: t(`formBuilder.${fieldData.maxLabel}`),
            };
            render = (
              <div className="range-wrapper mb-3 px-3">
                <p className="text-center">{placeholder}</p>
                <Slider
                  marks={marks}
                  onChange={(value) => {
                    updateFormData(fieldName, value.toString(), fieldGroup);
                    props.propagateInputData(fieldName, value.toString(), fieldGroup);
                  }}
                  value={(formData[fieldGroup] && formData[fieldGroup][fieldName]) ? parseInt(formData[fieldGroup][fieldName].value) : 0}
                  min={fieldData.min}
                  max={fieldData.max}
                  defaultValue={fieldData.defaultValue}
                  step={fieldData.step}
                  tooltip={{ open: false }}
                />
              </div>
            );
            break;
          default:
            return null;
        }

        return (
          <Col className="mt-4" key={fieldName} span={12}>
            {render}
          </Col>
        );
      });
  }
  return (
    <>
      {createFields(props.fields, props.fieldsGroup)}
    </>
  );
};

export default FieldsCreator;
